/**
 * Reconnection Manager
 * Extracted from WebSocketManager.ts
 * Handles reconnection logic with exponential backoff
 */

import { log } from "@/shared/utils/logger";

export interface ReconnectionConfig {
  reconnectInterval: number;
  maxReconnectDelay: number;
  maxRetries: number;
}

export class ReconnectionManager {
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000; // Start with 1 second
  private config: ReconnectionConfig;

  constructor(config: ReconnectionConfig) {
    this.config = config;
  }

  // Schedule reconnection with exponential backoff
  scheduleReconnect(
    hasCredentials: boolean,
    reconnectFn: () => void
  ): boolean {
    // Don't reconnect if we don't have valid credentials
    if (!hasCredentials) {
      log.debug("WebSocket", "No valid credentials, skipping reconnect");
      return false;
    }

    // Don't reconnect if we've exceeded max retries (default: unlimited)
    const maxRetries = this.config.maxRetries || Infinity;
    if (this.reconnectAttempts >= maxRetries) {
      log.warn("WebSocket", "Max reconnection attempts reached, stopping");
      return false;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Exponential backoff with jitter
    const baseDelay = this.config.reconnectInterval || 1000;
    const maxDelay = this.config.maxReconnectDelay || 30000;

    // Calculate exponential backoff with jitter to prevent thundering herd
    this.reconnectDelay = Math.min(
      baseDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000,
      maxDelay
    );

    this.reconnectAttempts++;

    // Only log first few attempts to reduce noise
    if (this.reconnectAttempts === 1) {
      log.info(
        "WebSocket",
        `Connection lost, attempting to reconnect in ${this.reconnectDelay}ms...`
      );
    } else if (this.reconnectAttempts === 5) {
      log.info(
        "WebSocket",
        `Still trying to reconnect (attempt ${this.reconnectAttempts}, delay: ${this.reconnectDelay}ms)...`
      );
    } else if (this.reconnectAttempts === 10) {
      log.warn(
        "WebSocket",
        `Unable to reconnect after 10 attempts, will continue trying (delay: ${this.reconnectDelay}ms)`
      );
    }

    this.reconnectTimer = setTimeout(() => {
      reconnectFn();
    }, this.reconnectDelay);

    return true;
  }

  // Reset reconnection attempts
  resetAttempts(): void {
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
  }

  // Clear reconnection timer
  clearTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Get current attempt count
  getAttemptCount(): number {
    return this.reconnectAttempts;
  }

  // Set attempt count (for max retries override)
  setAttemptCount(count: number): void {
    this.reconnectAttempts = count;
  }
}
